import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlsoPhase2 } from "@/components/AlsoPhase2";
import { StorefrontIcon as Storefront, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ClockIcon as Clock, WarningCircleIcon as WarningCircle, PlugsConnectedIcon as PlugsConnected, ArrowSquareOutIcon as ArrowSquareOut, InfoIcon as Info, ShieldWarningIcon as ShieldWarning, MagnifyingGlassIcon as MagnifyingGlass, DatabaseIcon as Database } from "@phosphor-icons/react";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT"); } catch { return s; }
};

const PREREQUISITI = [
  "Creare un utente dedicato esclusivamente alle API (non usare utenti personali o condivisi)",
  "Naming consigliato: api.user@dominioazienda.it",
  "Creare l'utente preferibilmente a livello reseller account",
  "Assegnare ruoli e permessi tramite RBAC",
  "Configurare la IP Allow List",
  "Chiedere ad ALSO Support la whitelist MFA per l'utente API",
  "Verificare che TecSuite esca verso ALSO con IP pubblico stabile",
  "Impostare preferibilmente la lingua dell'utente API in inglese (evita field name localizzati)",
  "Verificare supporto TLS/cipher moderni lato server TecSuite",
];

export function AlsoConnectorCard() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discovery, setDiscovery] = useState([]);
  const [discBusy, setDiscBusy] = useState(null);
  const [discParams, setDiscParams] = useState({ parentAccountId: "", resellerContext: "", marketplaceId: "" });

  const loadCfg = () => api.get("/integrations/also/config").then(({ data }) => setCfg(data)).catch(() => {});
  const loadLogs = () => api.get("/integrations/also/logs?limit=15").then(({ data }) => setLogs(data)).catch(() => {});

  useEffect(() => { loadCfg(); loadLogs(); }, []);

  const openModal = () => {
    setForm({
      name: cfg.name || "ALSO Cloud Marketplace",
      base_url: cfg.base_url || "",
      api_prefix: cfg.api_prefix || "",
      login_endpoint: cfg.login_endpoint || "",
      username: cfg.username || "",
      reseller_account_id: cfg.reseller_account_id || "",
      reseller_context: cfg.reseller_context || "",
      parent_account_id_default: cfg.parent_account_id_default || "",
      marketplace_id_primary: cfg.marketplace_id_primary || "",
      marketplace_name_primary: cfg.marketplace_name_primary || "",
      numeric_id: cfg.numeric_id || "",
      parent_numeric_id: cfg.parent_numeric_id || "",
      upstream_parent_account_id: cfg.upstream_parent_account_id || "",
      vat_id: cfg.vat_id || "",
      currency: cfg.currency || "",
      account_type: cfg.account_type || "",
      customer_id: cfg.customer_id || "",
      mpn_id: cfg.mpn_id || "",
      contract_id: cfg.contract_id || "",
      password: "",
      timeout_seconds: cfg.timeout_seconds ?? 30,
      verify_ssl: cfg.verify_ssl ?? true,
      debug_log: cfg.debug_log ?? false,
      enabled: cfg.enabled ?? false,
      ip_allowlist_notes: cfg.ip_allowlist_notes || "",
      mfa_whitelist_requested: cfg.mfa_whitelist_requested ?? false,
      operational_notes: cfg.operational_notes || "",
      swaggerhub_url: cfg.swaggerhub_url || "",
    });
    setOpen(true);
  };

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, timeout_seconds: Number(form.timeout_seconds) || 30 };
      if (!payload.password) delete payload.password;
      const { data } = await api.put("/integrations/also/config", payload);
      setCfg(data);
      toast.success("Configurazione ALSO salvata");
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/also/test");
      if (data.ok) toast.success("Connessione riuscita", { description: data.message });
      else toast.error("Connessione fallita", { description: data.message });
      loadCfg(); loadLogs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const setDP = (k) => (e) => setDiscParams((p) => ({ ...p, [k]: e.target.value }));

  const runDiscovery = async (action, extra = {}) => {
    setDiscBusy(action);
    try {
      const { data } = await api.post(`/integrations/also/discovery/${action}`, extra);
      setDiscovery((prev) => [{ _action: action, _at: new Date().toISOString(), ...data }, ...prev].slice(0, 8));
      if (data.ok) toast.success(`Discovery "${action}"`, { description: `${data.count ?? "?"} record` });
      else toast.error(`Discovery "${action}" fallita`, { description: data.message });
      loadCfg(); loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : `Impossibile eseguire la discovery "${action}".`); }
    finally { setDiscBusy(null); }
  };

  if (!cfg) return null;

  const configured = cfg.password_configured && cfg.enabled;
  const statusBadge = cfg.last_test_status === "success"
    ? <Badge variant="outline" className="rounded-sm bg-green-100 text-green-800 border-green-200 gap-1"><CheckCircle size={13} weight="fill" /> Connessione OK</Badge>
    : cfg.last_test_status === "error"
      ? <Badge variant="outline" className="rounded-sm bg-red-100 text-red-800 border-red-200 gap-1"><XCircle size={13} weight="fill" /> Errore</Badge>
      : configured
        ? <Badge variant="outline" className="rounded-sm bg-blue-100 text-blue-800 border-blue-200 gap-1"><Clock size={13} /> Configurato</Badge>
        : <Badge variant="outline" className="rounded-sm bg-zinc-100 text-zinc-600 gap-1"><XCircle size={13} /> Non configurato</Badge>;

  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="also-connector-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Storefront size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">{cfg.name || "ALSO Cloud Marketplace"}</h3>
            <p className="text-xs text-muted-foreground">Connettore ACMP · Simple API session-based. Fonte per anagrafiche e licenze Microsoft 365 / Adobe (import nelle fasi successive).</p>
          </div>
        </div>
        {statusBadge}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Abilitato</span><span>{cfg.enabled ? "Sì" : "No"}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Credenziali</span><span data-testid="also-password-set">{cfg.password_configured ? "Configurate" : "Non configurate"}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Username API</span><span className="font-mono text-xs">{cfg.username_masked || "—"}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Ultimo test</span><span data-testid="also-last-test">{fmtDate(cfg.last_test_at)}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Verifica SSL</span><span>{cfg.verify_ssl ? "Attiva" : <span className="text-amber-700 font-medium">Disattivata</span>}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Base URL</span><span className="font-mono text-xs truncate max-w-[220px]">{cfg.base_url || "—"}</span></div>
      </div>

      {!cfg.verify_ssl && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 mb-4 flex items-start gap-2" data-testid="also-ssl-warning">
          <ShieldWarning size={15} className="mt-0.5 shrink-0" />
          <span>Verifica SSL disattivata: usare solo per debug temporaneo. Riattivarla in produzione.</span>
        </div>
      )}

      {cfg.last_error && (
        <div className="rounded-sm bg-red-50 border border-red-200 text-red-800 text-xs p-3 mb-4 flex items-start gap-2" data-testid="also-last-error">
          <WarningCircle size={15} className="mt-0.5 shrink-0" />
          <div>
            <span>{cfg.last_error}</span>
            {cfg.last_error_category && <span className="block text-[11px] opacity-70 mt-0.5">Categoria: {cfg.last_error_category}</span>}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button onClick={openModal} className="rounded-sm" data-testid="also-configura-btn">Configura</Button>
        <Button variant="outline" onClick={test} disabled={testing || !cfg.password_configured} className="rounded-sm gap-2" data-testid="also-test-btn">
          <PlugsConnected size={16} /> {testing ? "Verifica..." : "Test connessione"}
        </Button>
        <a href={cfg.swaggerhub_url || "#"} target="_blank" rel="noreferrer" data-testid="also-swagger-link">
          <Button variant="ghost" className="rounded-sm gap-2 text-muted-foreground"><ArrowSquareOut size={16} /> Documentazione SwaggerHub</Button>
        </a>
      </div>

      {/* FASE 2A — Discovery API (read-only) */}
      {configured && (
        <div className="mt-5 border border-border rounded-sm p-4" data-testid="also-discovery-section">
          <div className="flex items-center gap-2 mb-1">
            <MagnifyingGlass size={16} className="text-primary" />
            <h4 className="font-heading font-semibold text-sm">Discovery API</h4>
            <Badge variant="outline" className="rounded-sm text-[10px] bg-blue-50 text-blue-700 border-blue-200">Read-only</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Esplora i dati disponibili via Simple API senza importare o modificare nulla nel gestionale. Utile per individuare marketplace, reseller context e parametri necessari alle fasi successive.</p>

          <div className="flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={!!discBusy} onClick={() => runDiscovery("marketplaces")} data-testid="also-disc-marketplaces-btn">
              <Storefront size={14} /> {discBusy === "marketplaces" ? "…" : "Leggi marketplaces"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={!!discBusy} onClick={() => runDiscovery("reseller_context")} data-testid="also-disc-reseller-btn">
              <Info size={14} /> {discBusy === "reseller_context" ? "…" : "Leggi account/reseller context"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={!!discBusy} onClick={() => runDiscovery("marketplace_services", discParams.marketplaceId ? { marketplaceId: discParams.marketplaceId } : {})} data-testid="also-disc-services-btn">
              <Database size={14} /> {discBusy === "marketplace_services" ? "…" : "Verifica servizi marketplace"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={!!discBusy} onClick={() => runDiscovery("subscriptions", { parentAccountId: discParams.parentAccountId, resellerContext: discParams.resellerContext })} data-testid="also-disc-subs-btn">
              <Database size={14} /> {discBusy === "subscriptions" ? "…" : "Verifica subscriptions"}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">parentAccountId</Label>
              <Input data-testid="also-disc-parent" value={discParams.parentAccountId} onChange={setDP("parentAccountId")} placeholder="AccountId cliente" className="mt-1 rounded-sm font-mono text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">resellerContext</Label>
              <Input data-testid="also-disc-reseller-ctx" value={discParams.resellerContext} onChange={setDP("resellerContext")} placeholder={cfg.reseller_account_id || "Reseller Account ID"} className="mt-1 rounded-sm font-mono text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">marketplaceId (opz.)</Label>
              <Input data-testid="also-disc-mp" value={discParams.marketplaceId} onChange={setDP("marketplaceId")} placeholder="auto: primo marketplace" className="mt-1 rounded-sm font-mono text-xs h-8" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3 italic">Se lasci vuoto <span className="font-mono">resellerContext</span>, viene usato il Reseller Account ID salvato in configurazione. Se mancano parametri obbligatori, l'esito lo indicherà chiaramente.</p>

          {discovery.length > 0 && (
            <div className="space-y-2" data-testid="also-discovery-results">
              {discovery.map((r, i) => (
                <div key={i} className="border border-border/60 rounded-sm p-3 bg-zinc-50/50" data-testid="also-discovery-result">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <div className="flex items-center gap-2 text-xs">
                      {r.ok
                        ? <span className="text-green-700 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> OK</span>
                        : <span className="text-red-700 flex items-center gap-1"><XCircle size={13} weight="fill" /> Errore</span>}
                      <span className="font-mono text-[11px] bg-secondary px-1.5 py-0.5 rounded-sm">{r.endpoint_logico || r._action}</span>
                      {r.http_status != null && <span className="text-muted-foreground">HTTP {r.http_status}</span>}
                      {r.count != null && <span className="text-muted-foreground">· {r.count} record</span>}
                      {r.duration_ms != null && <span className="text-muted-foreground">· {r.duration_ms} ms</span>}
                    </div>
                  </div>
                  {r.message && <p className="text-[11px] text-muted-foreground mb-1">{r.message}</p>}
                  {r.missing_params?.length > 0 && (
                    <p className="text-[11px] text-amber-700 mb-1">Parametri mancanti: {r.missing_params.join(", ")}</p>
                  )}
                  {r.id_candidates && Object.keys(r.id_candidates).length > 0 && (
                    <div className="text-[11px] mb-1">
                      <span className="text-muted-foreground">ID candidati:</span>{" "}
                      {Object.entries(r.id_candidates).slice(0, 10).map(([k, v]) => (
                        <span key={k} className="font-mono bg-secondary px-1 py-0.5 rounded-sm mr-1 inline-block mb-1">{k}={v}</span>
                      ))}
                    </div>
                  )}
                  {(r.preview != null) && (
                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-muted-foreground">Preview JSON (sanitizzata)</summary>
                      <pre className="mt-1 max-h-56 overflow-auto bg-zinc-900 text-zinc-100 rounded-sm p-2 text-[10px] leading-relaxed">{JSON.stringify(r.preview, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {configured && <AlsoPhase2 />}

      {logs.length > 0 && (
        <div className="mt-5 border-t border-border pt-3" data-testid="also-logs-section">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Log connessione</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr className="text-left border-b border-border/60">
                  <th className="py-1 pr-3 font-medium">Data/ora</th>
                  <th className="py-1 pr-3 font-medium">Azione</th>
                  <th className="py-1 pr-3 font-medium">Stato</th>
                  <th className="py-1 pr-3 font-medium">HTTP</th>
                  <th className="py-1 pr-3 font-medium">Durata</th>
                  <th className="py-1 pr-3 font-medium">Categoria</th>
                  <th className="py-1 font-medium">Messaggio</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border/40" data-testid="also-log-row">
                    <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(l.started_at)}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{l.endpoint_logico || l.action}</td>
                    <td className="py-1 pr-3">
                      {l.status === "success"
                        ? <span className="text-green-700 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> OK</span>
                        : <span className="text-red-700 flex items-center gap-1"><XCircle size={12} weight="fill" /> Errore</span>}
                    </td>
                    <td className="py-1 pr-3">{l.http_status ?? "—"}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{l.duration_ms != null ? `${l.duration_ms} ms` : "—"}</td>
                    <td className="py-1 pr-3">{l.error_category || "—"}</td>
                    <td className="py-1 text-muted-foreground">{l.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="also-config-modal">
          <DialogHeader>
            <DialogTitle>Configura ALSO Cloud Marketplace</DialogTitle>
            <DialogDescription>Simple API session-based. La password/secret non viene mai mostrata dopo il salvataggio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* Nota ambiente */}
            <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 flex items-start gap-2">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>Cloud Marketplace non fornisce più un ambiente PreProduction dedicato. SwaggerHub può essere usato per consultare metodi, parametri, request/response e simulazioni dove supportato. I test reali TecSuite devono essere limitati al solo login/token e non devono creare o modificare dati.</span>
            </div>

            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <span className="text-sm">Abilitato</span>
              <Switch data-testid="also-f-enabled" checked={!!form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nome connettore</Label>
              <Input data-testid="also-f-name" value={form.name} onChange={setF("name")} className="mt-1 rounded-sm text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider">Base URL marketplace</Label>
                <Input data-testid="also-f-base_url" value={form.base_url} onChange={setF("base_url")} placeholder="https://marketplace.alsoitaly.it" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">API prefix</Label>
                <Input data-testid="also-f-api_prefix" value={form.api_prefix} onChange={setF("api_prefix")} placeholder="/SimpleAPI/SimpleAPIService.svc/rest" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Login endpoint</Label>
                <Input data-testid="also-f-login_endpoint" value={form.login_endpoint} onChange={setF("login_endpoint")} placeholder="GetSessionToken" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Username API</Label>
                <Input data-testid="also-f-username" value={form.username} onChange={setF("username")} placeholder="api.user@dominioazienda.it" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Reseller Account ID / Context</Label>
                <Input data-testid="also-f-reseller_account_id" value={form.reseller_account_id} onChange={setF("reseller_account_id")} placeholder="es. 1727168" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Reseller Context</Label>
                <Input data-testid="also-f-reseller_context" value={form.reseller_context} onChange={setF("reseller_context")} placeholder="es. 1727168" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Parent Account ID default</Label>
                <Input data-testid="also-f-parent_account_id_default" value={form.parent_account_id_default} onChange={setF("parent_account_id_default")} placeholder="es. 1727168" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Upstream Parent Account ID</Label>
                <Input data-testid="also-f-upstream_parent_account_id" value={form.upstream_parent_account_id} onChange={setF("upstream_parent_account_id")} placeholder="es. 1172448 (metadato)" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Marketplace ID primario</Label>
                <Input data-testid="also-f-marketplace_id_primary" value={form.marketplace_id_primary} onChange={setF("marketplace_id_primary")} placeholder="es. 99584" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nome marketplace primario</Label>
                <Input data-testid="also-f-marketplace_name_primary" value={form.marketplace_name_primary} onChange={setF("marketplace_name_primary")} placeholder="es. Listino Tecnologie Srl" className="mt-1 rounded-sm text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Numeric ID</Label>
                <Input data-testid="also-f-numeric_id" value={form.numeric_id} onChange={setF("numeric_id")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Parent Numeric ID</Label>
                <Input data-testid="also-f-parent_numeric_id" value={form.parent_numeric_id} onChange={setF("parent_numeric_id")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Customer ID</Label>
                <Input data-testid="also-f-customer_id" value={form.customer_id} onChange={setF("customer_id")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">MPN ID</Label>
                <Input data-testid="also-f-mpn_id" value={form.mpn_id} onChange={setF("mpn_id")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Contract ID</Label>
                <Input data-testid="also-f-contract_id" value={form.contract_id} onChange={setF("contract_id")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">VAT ID</Label>
                <Input data-testid="also-f-vat_id" value={form.vat_id} onChange={setF("vat_id")} placeholder="es. IT04178600237" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Currency</Label>
                <Input data-testid="also-f-currency" value={form.currency} onChange={setF("currency")} placeholder="EUR" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Account Type</Label>
                <Input data-testid="also-f-account_type" value={form.account_type} onChange={setF("account_type")} placeholder="Reseller" className="mt-1 rounded-sm text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Password / API secret</Label>
                <Input data-testid="also-f-password" type="password" value={form.password} onChange={setF("password")} placeholder={cfg.password_configured ? "•••••• (invariata)" : ""} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Timeout (secondi)</Label>
                <Input data-testid="also-f-timeout_seconds" type="number" min={1} max={300} value={form.timeout_seconds} onChange={setF("timeout_seconds")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
            </div>

            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div className="text-sm">Verifica SSL <span className="block text-xs text-muted-foreground">Attiva di default — disattivare solo per debug</span></div>
              <Switch data-testid="also-f-verify_ssl" checked={!!form.verify_ssl} onCheckedChange={(v) => setForm((f) => ({ ...f, verify_ssl: v }))} />
            </div>
            {!form.verify_ssl && (
              <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2.5 flex items-start gap-2">
                <ShieldWarning size={14} className="mt-0.5 shrink-0" /><span>Attenzione: verifica SSL disattivata (rischio sicurezza, solo debug temporaneo).</span>
              </div>
            )}
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div className="text-sm">Debug log <span className="block text-xs text-muted-foreground">Salva dettagli tecnici (mai password/token)</span></div>
              <Switch data-testid="also-f-debug_log" checked={!!form.debug_log} onCheckedChange={(v) => setForm((f) => ({ ...f, debug_log: v }))} />
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div className="text-sm">MFA whitelist richiesta <span className="block text-xs text-muted-foreground">Segnala se l'utente API richiede whitelist MFA lato ALSO</span></div>
              <Switch data-testid="also-f-mfa_whitelist_requested" checked={!!form.mfa_whitelist_requested} onCheckedChange={(v) => setForm((f) => ({ ...f, mfa_whitelist_requested: v }))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">IP pubblico autorizzato / note IP Allow List</Label>
              <Textarea data-testid="also-f-ip_allowlist_notes" value={form.ip_allowlist_notes} onChange={setF("ip_allowlist_notes")} rows={2} className="mt-1 rounded-sm text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Note operative</Label>
              <Textarea data-testid="also-f-operational_notes" value={form.operational_notes} onChange={setF("operational_notes")} rows={2} className="mt-1 rounded-sm text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Link documentazione SwaggerHub</Label>
              <Input data-testid="also-f-swaggerhub_url" value={form.swaggerhub_url} onChange={setF("swaggerhub_url")} className="mt-1 rounded-sm font-mono text-xs" />
            </div>

            {/* Box prerequisiti */}
            <div className="rounded-sm border border-border p-3" data-testid="also-prereq-box">
              <p className="text-sm font-medium mb-2">Prerequisiti utente API ALSO</p>
              <ul className="space-y-1">
                {PREREQUISITI.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle size={13} className="mt-0.5 shrink-0 text-primary/70" /><span>{t}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-muted-foreground mt-2 italic">Checklist informativa: non blocca il salvataggio della configurazione.</p>
            </div>

            {/* Info fasi successive */}
            <div className="rounded-sm bg-zinc-50 border border-border p-3" data-testid="also-future-note">
              <p className="text-sm font-medium mb-1">Informazioni per fasi successive</p>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-muted-foreground">
                <li>Per leggere servizi/subscription serviranno AccountId, reseller context, ServiceName/ServiceId e field name specifici.</li>
                <li>Il ServiceName tecnico ha formato tipo <span className="font-mono">123456_servicename_123456</span>.</li>
                <li>I ServiceName/ServiceId si recuperano dalla pagina servizio Marketplace, dal Pricelist Export (colonna ServiceId) o via API dedicate.</li>
                <li>I field name possono dipendere dal servizio e, raramente, dalla lingua dell'utente API.</li>
                <li>Per creare subscription Microsoft serve gestire il Purchase Order Commitment quando <span className="font-mono">RequiresPurchaseOrderCommitment = true</span>.</li>
              </ul>
              <p className="text-[11px] text-muted-foreground mt-1 italic">Nota solo informativa: non implementata in FASE 1.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="also-save-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
